import { Component, output, input, OnInit, effect } from '@angular/core';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { Tag } from 'src/app/entities/tag/tag';
import { Project } from 'src/app/entities/project/project';
import { TimeTrack, TrackPermissions } from 'src/app/entities/time-track/time-track';
import { Uuid } from 'src/app/entities/value-objects/uuid/uuid';
import { BasicDataService } from 'src/app/services/basic-data/basic-data.service';
import { DateService } from 'src/app/services/date-service/date.service';
import { TimeTrackRepository } from 'src/app/services/remote-data-service/repositories/time-track-repository/time-track.repository';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { CommonModule } from '@angular/common';
import { Observable } from 'rxjs';
import { catchError, map, startWith } from 'rxjs/operators';
import log from 'loglevel';
import { SnackbarService } from 'src/app/services/snackbar-service/snackbar.service';
import { User } from 'src/app/entities/user/user';
import { StoreService } from 'src/app/services/store-service/store.service';
import { SelectListComponent } from '../select-list/select-list.component';
import { BaseComponent } from '../base-component/base.component';


/**
 * Component used to display a form to add new time-tracks to the database.
 */
@Component({
  selector: 'app-time-entry-input',
  templateUrl: './time-entry-input.component.html',
  styleUrls: ['./time-entry-input.component.scss'],
  providers: [
    BasicDataService
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    MatAutocompleteModule,
    SelectListComponent
  ]
})
export class TimeEntryInputComponent extends BaseComponent implements OnInit {

  /**
   * A logger instance for logging component activity.
   */
  protected override logger = log.getLogger('TimeEntryInput');

  /**
   * Property to store the value of the selected project.
   */
  selectedProjectValue: Project | undefined;

  /**
   * Observable list of options to display on the autocomplete input.
   */
  filteredOptions: Observable<string[]> = new Observable<string[]>();

  /**
   * Input that receives an array with all the projects.
   */
  projects = input.required<Project[]>();


  /**
   * Input that receives an array with all the tags.
   */
  tags = input.required<Tag[]>();


  /**
   * Get the current `datetime`.
   */
  currentDate = new Date(); // Current date


  /**
   * `ReactiveForms` form to manage all the `time-entry-input` inputs.
   */
  public form = new FormGroup({

    // Input of type `text`.
    inputText: new FormControl(''),

    // Input of type `datetime-local`, `getLocalIsosString` necessary to set
    // default values.
    startDate: new FormControl(this.dateService.getLocalIsosString(this.currentDate), Validators.required),

    // Input of type `datetime-local`, `getLocalIsosString` necessary to set
    // default values.
    endDate: new FormControl(this.dateService.getLocalIsosString(this.dateService.getProxDate(this.currentDate, 1)),
      Validators.required),

    // Input of type `boolean`
    isBillable: new FormControl(false),

    // Input of type `Tag[]`.
    selectedTags: new FormControl<string[]>([]),

    // Input of type `Project`.
    selectedProject: new FormControl<string | null>(null),
  });


  /**
   * Output that emits the new `TimeTrack` object to the parent component.
   */
  enterPressed = output<TimeTrack>();


  /**
   * The current user logged in the app.
   */
  protected currentUser?: User;


  /**
   * The current user logged in the app.
   */
  protected currentUser$: Observable<User | null>;


  /**
   * List of track suggestions for the autocomplete.
   */
  trackSuggestions: string[] = [];


  /**
   * Component constructor.
   *
   * @param logger Logger service to display useful logs.
   *
   * @param basicDataService Service used to interact with entities queries.
   *
   * @param dateService Service to use custom date functions.
   */
  constructor(
    protected timeTrackRep: TimeTrackRepository,
    protected dateService: DateService,
    protected snackBar: SnackbarService,
    protected store: StoreService,
  ) {
    super();

    this.currentUser$ = store.currentUser;

    this.currentUser$.pipe(
      catchError(() => {
        return [];
      })
    ).subscribe((user) => {
      this.currentUser = user || undefined;
    })

    this.reset();

    effect(() => {
      this.setOptionsFromTags(this.tags());
      this.setOptionsFromProjects(this.projects());
    })
  }


  /**
   * Angular component initialize hook.
   *
   * Sets some form inputs default values.
   */
  ngOnInit(): void {
    this.form.patchValue({
      startDate: this.dateService.getLocalIsosString(this.currentDate),
      endDate: this.dateService.getLocalIsosString(this.dateService.getProxDate(this.currentDate, 1))
    });

    // Subscribe to changes in the selectedProject field
    this.form.get('selectedProject')?.valueChanges.subscribe(value => {
      this.selectedProjectValue = this.getProjectFromString(value) || undefined;
      this.timeTrackRep.retrieveAll({ projects: this.selectedProjectValue?.id }).subscribe(suggestions => {
        this.trackSuggestions = suggestions.map(suggestion => suggestion.title);
      });
    });

    // Subscribe to changes in the inputText field for autocomplete suggestions
    this.filteredOptions = this.form.get('inputText')?.valueChanges.pipe(
      startWith(''),
      map(value => this._filter(value || ''))
    ) as Observable<string[]>;
  }


  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();

    return this.trackSuggestions.filter(option => option.toLowerCase().includes(filterValue));
  }


  /**
   * Retrieves Tag objects from an array of tag IDs.
   *
   * @param string: string[] - An array of tag IDs.
   */
  getTagsFromStrings(string: string[] | null | undefined): Tag[] {
    if (!string) {
      return [];
    }

    return this.tags().filter(tag => string.includes(tag.id))
  }

  /**
   * Retrieves Project objects from a string.
   *
   * @param string: string - project id.
   */
  getProjectFromString(string: string | null | undefined): Project | null | undefined {
    return this.projects().find(project => project.id === string)
  }


  /**
   * Resets the form inputs values except the date ones.
   */
  reset(): void {
    this.form.get('inputText')?.reset();
    this.form.get('isBillable')?.reset();
    this.form.get('selectedTags')?.reset();
    this.form.get('selectedProject')?.reset();
    this.form.get('duration')?.reset();
  }


  /**
   * It builds the `TimeTrack` entity, creates the track and emits the data.
   *
   * - It first creates the entire object.
   * - Then does the query request to create the track in the db.
   * - And finally emits the new object to the parent component.
   */
  buildTrackEntryData(): void {
    const values = this.form.value;

    this.logger.trace('Building track entry data', values);

    const name = values['inputText'] || '';
    const from = new Date(values['startDate'] || new Date());
    const to = new Date(values['endDate'] || new Date());
    const permissions: TrackPermissions = {
      delete: false,
      update: false
    }
    const project = this.getProjectFromString(values.selectedProject) || undefined;
    const tags = this.getTagsFromStrings(values.selectedTags);

    const timeTrack = new TimeTrack(
      Uuid.generateId(),
      name,
      from,
      to,
      permissions,
      project,
      this.currentUser,
      tags,
    );

    this.logger.trace('Track entry data built', timeTrack);
    this.timeTrackRep.addNewTrack(timeTrack).pipe(
      catchError(() => {
        return []
      })
    ).subscribe(() => {
      this.enterPressed.emit(timeTrack);
    });
  }

  /**
   * Function event that runs when form is submitted. Emits the form data.
   *
   * It resets the form inputs and emits the form data to parent component.
   */
  onEnter(): void {
    this.buildTrackEntryData();
    this.reset();
  }
}
